import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Page from '../pages/Page'
import useQueryLibrary from '../../hooks/useQueryLibrary'
import FetchContext from '../../context/fetch/FetchContext'
import { useNotificationContext } from '../../units/notifications/NotificationContext'
import { resolveErrorMessage } from '../../helpers/error.helper'
import useAnnouncementPreview from './useAnnouncementPreview'
import AnnouncementsTable from './AnnouncementsTable'
import AnnouncementPopup from './AnnouncementPopup'
import PublishConfirmation from './PublishConfirmation'
import { NEW_ANNOUNCEMENT } from './AnnouncementEditorPage'
import { resolveAnnouncementRows } from '../../data-organizers/announcements/resolveAnnouncementRows'
import useRenderTrace from '../../units/debug/useRenderTrace'

export default function AnnouncementsPage() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const queryLibrary = useQueryLibrary()
    const fetcher = useContext(FetchContext)
    const { addNotification } = useNotificationContext()
    const { preview, openPreview, closePreview } = useAnnouncementPreview()
    const [publishing, setPublishing] = useState(null)

    const {
        data: result,
        isFetching,
        isLoading,
    } = useQuery({
        queryKey: queryLibrary.announcementsQuery(),
        queryFn: () => fetcher.fetchAnnouncements(),
    })

    useRenderTrace('AnnouncementsPage', {
        navigate,
        queryClient,
        fetcher,
        addNotification,
        preview,
        openPreview,
        closePreview,
        publishing,
        result,
        isFetching,
        isLoading,
    })

    const refresh = () => queryClient.invalidateQueries({
        queryKey: queryLibrary.announcementsQuery(),
    })

    const report = (error) => {
        addNotification({
            type: 'error',
            content: resolveErrorMessage(error),
        })
    }

    const publishMutation = useMutation({
        mutationFn: (row) => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${row.id}/publish`,
        }),
        onSuccess: refresh,
        onError: report,
    })

    const unpublishMutation = useMutation({
        mutationFn: (row) => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${row.id}/unpublish`,
        }),
        onSuccess: refresh,
        onError: report,
    })

    const confirmPublish = () => {
        const row = publishing

        setPublishing(null)
        publishMutation.mutate(row)
    }

    const cloneMutation = useMutation({
        mutationFn: (row) => fetcher.authenticatedFetch({
            method: 'post',
            path: `/announcements/${row.id}/clone`,
        }),
        onSuccess: async (clone) => {
            await refresh()
            navigate(`/announcements/${clone.documentId}`)
        },
        onError: report,
    })

    const commands = [
        {
            scheme: 'link',
            label: 'מודעה חדשה',
            to: `/announcements/${NEW_ANNOUNCEMENT}`,
            position: 'start',
        },
    ]

    const busy = isFetching
        || isLoading
        || publishMutation.isPending
        || unpublishMutation.isPending
        || cloneMutation.isPending

    return (
        <Page
            commands={commands}
            className="announcements"
            title="מודעות"
            busy={busy}
        >
            <AnnouncementsTable
                rows={resolveAnnouncementRows(result)}
                onEdit={(row) => navigate(`/announcements/${row.id}`)}
                onClone={(row) => cloneMutation.mutate(row)}
                onPublish={(row) => setPublishing(row)}
                onUnpublish={(row) => unpublishMutation.mutate(row)}
                onPreview={(row) => openPreview(row.id)}
                onStats={(row) => navigate(`/announcements/${row.id}/stats`)}
            />
            <PublishConfirmation
                open={publishing != null}
                isContentLocked={publishing?.isContentLocked ?? false}
                onConfirm={confirmPublish}
                onCancel={() => setPublishing(null)}
            />
            <AnnouncementPopup
                workingSet={preview}
                onClose={closePreview}
            />
        </Page>
    )
}
